import * as Popover from '@radix-ui/react-popover';
import { Check, ChevronDown } from 'lucide-react';
import { useState } from 'react';

import { useDialogPortalContainer } from './Dialog.js';
import { selectionSummary, toggleSelection, type MultiSelectOption } from './multi-select.logic.js';
import { CONTROL_SHELL, useFieldInvalid } from './primitives.js';

export type { MultiSelectOption };

/**
 * A `<select>` that takes several values — the report bar's Distributor filter.
 *
 * NOT `<select multiple>`. On a desktop that renders as an always-open list box
 * that is ctrl-click to add, which nobody discovers, and it cannot sit in a
 * one-line filter row; on a phone every browser renders it differently. A
 * button that opens a list of checkboxes reads the same everywhere and says
 * what it does.
 *
 * CLOSED, IT READS LIKE THE `Select` IT REPLACED: the same frame, the "all"
 * wording when nothing is chosen, the option's own label when one is, and a
 * count from two up — see `selectionSummary` for why not the joined names.
 *
 * EACH TICK APPLIES AT ONCE, like every other control in the filter bar. An
 * "Terapkan" button would make this the one filter that does nothing until a
 * second click, and the list stays open between ticks, so choosing three is
 * three taps rather than three trips.
 *
 * PORTALLED, like `Combobox`, which is also what keeps the checkboxes' own
 * `<label>`s out of the `Field` label this control sits in — a label inside a
 * label hands its click to the outer one's control.
 */
export function MultiSelect({
  options,
  value,
  onChange,
  allLabel,
  noun,
  disabled = false,
}: {
  options: readonly MultiSelectOption[];
  /** The chosen ids. Empty means "all". */
  value: readonly string[];
  onChange: (value: string[]) => void;
  /** The empty selection's wording, and the row that clears — "Semua Distributor". */
  allLabel: string;
  /** What the count counts — "Distributor" gives "2 Distributor dipilih". */
  noun: string;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const container = useDialogPortalContainer();
  const invalid = useFieldInvalid();
  const chosen = new Set(value);
  const nothingChosen = value.length === 0;

  return (
    <Popover.Root open={open && !disabled} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          disabled={disabled}
          aria-invalid={invalid || undefined}
          className={`focus-ring ${CONTROL_SHELL} flex items-center gap-2 px-3 text-left disabled:bg-panel disabled:text-muted`}
        >
          <span className="min-w-0 flex-1 truncate">
            {selectionSummary(options, value, allLabel, noun)}
          </span>
          <ChevronDown size={16} className="shrink-0 text-steel" aria-hidden />
        </button>
      </Popover.Trigger>

      <Popover.Portal container={container}>
        <Popover.Content
          side="bottom"
          align="start"
          sideOffset={4}
          collisionPadding={8}
          // At least the trigger's width, wider when a label needs it — a grid
          // column on a desktop is narrower than `900024 - BANDUNG`.
          className="surface z-50 min-w-[var(--radix-popover-trigger-width)] max-w-[var(--radix-popover-content-available-width)] overflow-hidden"
        >
          <button
            type="button"
            onClick={() => onChange([])}
            className="tap-target flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-ink hover:bg-tint"
          >
            <Check
              size={16}
              className={`shrink-0 ${nothingChosen ? 'text-primary-700' : 'invisible'}`}
              aria-hidden
            />
            {allLabel}
          </button>

          <ul
            aria-label={noun}
            className="max-h-[min(18rem,var(--radix-popover-content-available-height))] overflow-y-auto border-t border-line py-1"
          >
            {options.map((option) => (
              <li key={option.id}>
                <label className="tap-target flex cursor-pointer items-center gap-2 px-3 py-2 hover:bg-tint">
                  <input
                    type="checkbox"
                    checked={chosen.has(option.id)}
                    onChange={() => onChange(toggleSelection(options, value, option.id))}
                    className="h-4 w-4 shrink-0 accent-primary-700"
                  />
                  <span className="min-w-0 flex-1 text-sm text-ink">{option.label}</span>
                </label>
              </li>
            ))}
          </ul>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
